/**
 * How a backtest run describes the inputs it actually used.
 *
 * Two places show this: the running-backtest status and the finished result
 * card. They used to format it differently and both assumed a parameter value
 * was a number, so a string, a flag, a list or a nested object came out as
 * cramped JSON or "[object Object]".
 */

export type ParameterDisplay =
  | { kind: 'scalar'; text: string }
  | { kind: 'nested'; summary: string; text: string };

/**
 * A strategy parameter is whatever the strategy author made it. A scalar gets
 * its value shown; anything nested says how big it is and carries its own
 * formatted value for the reader to open.
 */
export function describeParameterValue(value: unknown): ParameterDisplay {
  if (value === null || value === undefined) return { kind: 'scalar', text: 'None' };
  if (typeof value === 'string') return { kind: 'scalar', text: value.trim() || 'Empty' };
  // Numbers, booleans and anything else that is not a container print as-is.
  if (!(value instanceof Object)) return { kind: 'scalar', text: String(value) };
  if (Array.isArray(value)) {
    return {
      kind: 'nested',
      summary: `${value.length} ${value.length === 1 ? 'item' : 'items'}`,
      text: JSON.stringify(value, null, 2),
    };
  }
  const fieldCount = Object.keys(value).length;
  return {
    kind: 'nested',
    summary: `${fieldCount} ${fieldCount === 1 ? 'field' : 'fields'}`,
    text: JSON.stringify(value, null, 2),
  };
}

export type ParameterRow = ParameterDisplay & { name: string };

export function parameterRows(values: unknown): ParameterRow[] {
  if (!(values instanceof Object)) return [];
  return Object.entries(values).map(([name, value]) => ({
    name,
    ...describeParameterValue(value),
  }));
}

// Vendor names that are acronyms, which title case would otherwise mangle.
const RUN_CONTEXT_ACRONYMS = new Set(['ccxt', 'ibkr', 'ib', 'api', 'tda', 'cme', 'nyse']);

function titleCaseRunContext(value: unknown): string {
  const cleaned = String(value || '').replace(/[-_]+/g, ' ');
  if (RUN_CONTEXT_ACRONYMS.has(cleaned.trim().toLowerCase())) return cleaned.trim().toUpperCase();
  return cleaned.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

type ObservedRoute = {
  exchange?: string | null;
  vendor?: string | null;
  adapter?: string | null;
};

/**
 * Where the data actually came from, and nothing else. The symbol, the bar
 * size and the routing policy version are plumbing rather than something a
 * reader of this card needs, and they made the line unreadable.
 */
export function formatRouteSource(route: ObservedRoute | null | undefined): string | null {
  const exchange = route?.exchange ? titleCaseRunContext(route.exchange) : null;
  const vendor = route?.vendor ? titleCaseRunContext(route.vendor) : null;
  const adapter = route?.adapter
    ? titleCaseRunContext(route.adapter.replace(/RoutingAdapter$/i, ''))
    : null;
  return [exchange, vendor || adapter].filter(Boolean).join(' via ') || adapter || null;
}

type DataProvenance = {
  selection?: string | null;
  observedRoutes?: ObservedRoute[] | null;
};

/**
 * One line: the router that was chosen, then what it actually reached.
 * Empty when the run recorded neither, so the section can be left out.
 */
export function formatDataUsed(
  provenance: DataProvenance | null | undefined,
  routingPolicySnapshot: { selection?: string | null } | null | undefined,
): string {
  const selection =
    provenance?.selection ||
    routingPolicySnapshot?.selection ||
    (routingPolicySnapshot ? 'BotSpot Auto' : null);
  const routes = provenance?.observedRoutes;
  const observed = Array.isArray(routes) ? routes : [];
  const sources = [...new Set(observed.map(formatRouteSource).filter(Boolean))];
  return [selection, ...sources].filter(Boolean).join(' · ');
}
